import { Injectable, Optional } from '@angular/core';
import { Location } from '@angular/common';
import { Subject } from 'rxjs/Subject';
import { AccountDataServiceProvider } from '@services/user/account.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { IonicStorageProvider } from '@mob-providers/ionic-storage/storage.service';
import { ValidationProvider } from '@services/helpers/validation.service';
import { AccessTokens, User, UserSubscription } from '@models/models';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { ModalController } from 'ionic-angular';
import { TermsUpdatedModalComponent } from '@mob-components/shared/terms-updated-modal/terms-updated-modal';
import { SettingsProvider } from '@services/settings/settings';
import { ReplaySubject } from 'rxjs';
import { AuthTokensService } from '../../../web/modern/auth-tokens.service';

declare const Intercom;

@Injectable()
export class AccountServiceProvider {
	authenticated = false;

	isAnnualDiscount: boolean;
	isMonthlyDiscount: boolean;
	isAnnualPromoOrDiscountEnabled: boolean;
	isMonthlyPromoOrDiscountEnabled: boolean;
	isSubscribedAnnual: boolean;
	isSubscribedMonthly: boolean;
	isTrialShown: Subject<boolean> = new Subject();
	removeBg: any;
	subscription: UserSubscription;
	subscriptionData: BehaviorSubject<any> = new BehaviorSubject(null);
	user: User;
	userData: BehaviorSubject<User> = new BehaviorSubject(null);
	navigationData: BehaviorSubject<any> = new BehaviorSubject({});

	private companyId$: ReplaySubject<number> = new ReplaySubject<number>(1);
	private paths = {
		USER_MENU_PATH: 'authenticatedUser',
		USER_HOME_PATH: 'authenticatedUser',
		SETTING_NAVBAR_PATH: 'authenticatedUser',
		ANONYMOUS_MENU_PATH: 'anonymousUser',
		ANONYMOUS_HOME_PATH: 'anonymousUser',
		ADMIN_MENU_PATH: 'adminUser',
		ADMIN_HOME_PATH: 'adminUser'
	};

	constructor(
		private accountDataService: AccountDataServiceProvider,
		private messageService: MessageService,
		private validationService: ValidationProvider,
		private location: Location,
		private settingsService: SettingsProvider,
		@Optional() private secureStorage: IonicStorageProvider,
		@Optional() private storageService: AuthTokensService,
		@Optional() private modalCtrl: ModalController
	) {}

	get userDataObservable() {
		return this.userData.asObservable();
	}

	get navDataObservable() {
		return this.navigationData.asObservable();
	}

	get userObj() {
		return this.user;
	}

	get userSubscription(): UserSubscription {
		return this.subscription;
	}

	get userSubscriptionObservable() {
		return this.subscriptionData.asObservable();
	}

	get trialPeriodObservable() {
		return this.isTrialShown;
	}

	emitUser(user): void {
		this.userData.next(user);
	}

	emitSubscriptionInfo(data): void {
		this.subscriptionData.next(data);
	}

	emitTrial(value: boolean): void {
		this.isTrialShown.next(value);
	}

	getUser() {
		return this.accountDataService
			.getUser()
			.catch(error => {
				this.messageService.showDefaultErrorMessage(error);
				return Observable.throw(error);
			})
			.map(data => this.getUserSuccessCallback(data));
	}

	getUserWeb() {
		return this.accountDataService
			.getUserWeb()
			.catch(error => {
				this.messageService.showDefaultErrorMessage(error);
				return Observable.throw(error);
			})
			.map(data => this.getUserWebSuccessCallback(data));
	}

	logoutMobile() {
		return this.accountDataService.logout().map(() => {
			this.secureStorage.removeTokens();
			this.setUserAuthenticated(false);
			this.emitUser(null);
			this.emitSubscriptionInfo(null);
			delete this.user;
		});
	}

	logoutWeb() {
		return this.accountDataService
			.logout()
			.catch(error => {
				this.messageService.showDefaultErrorMessage(error);
				return Observable.throw(error);
			})
			.subscribe(() => this.successLogoutWeb());
	}

	successLogoutWeb() {
		this.setUserAuthenticated(false);
		this.setNavigationSettings(null, null, null);
		this.emitUser(null);
		this.emitSubscriptionInfo(null);
		this.storageService.removeTokens();
		delete this.user;
		this.location.go('/login');
		Intercom('shutdown');
	}

	isUserAuthenticated() {
		return this.authenticated;
	}

	isAdmin() {
		return this.user && this.user.admin;
	}

	setUserAuthenticated(isAuthenticated: boolean) {
		this.authenticated = isAuthenticated;
	}

	getEditableUser(
		getEditableUserSuccessCallback,
		getEditableUserErrorCallback
	) {
		this.accountDataService
			.getUserWeb()
			.subscribe(
				result => getEditableUserSuccessCallback(result),
				result => getEditableUserErrorCallback(result)
			);
	}

	authenticateMobile(
		email: string,
		password: string
	): Observable<AccessTokens> {
		if (
			this.validationService.isStringEmpty(password) ||
			this.validationService.isStringEmpty(email)
		) {
			this.messageService.showToaster('e_4', 'login');
			return;
		}
		return this.accountDataService.authenticateMobile(email, password);
	}

	authenticateWeb(
		email: string,
		password: string,
		stayLogin: boolean
	): Observable<any> {
		// ): Observable<HttpEvent<any>> {
		if (
			this.validationService.isStringEmpty(password) ||
			this.validationService.isStringEmpty(email)
		) {
			this.messageService.showToaster('e_0', 'login');
			return Observable.throw(new Error('validation error'));
		}
		return (
			this.accountDataService
				.authenticateWeb(email, password, stayLogin)
				// .map(data => this.getUserWebSuccessCallback(data))
				.catch(error => this.errorAuthenticate(error))
		);
	}

	getRefreshedAuthTokenWeb(token: string): Observable<any> {
		return this.accountDataService.getRefreshedAuthTokenWeb(token);
	}

	getRefreshedAuthToken(token: string): Observable<any> {
		return this.accountDataService.getRefreshedAuthToken(token);
	}

	signUp(signUpData, signUpSuccessCallback, signUpErrorCallback) {
		this.accountDataService
			.signUp(signUpData)
			.subscribe(
				data => signUpSuccessCallback(data),
				result => signUpErrorCallback(result)
			);
	}

	confirmSignUp(signUpSuccessCallback, signUpErrorCallback) {
		const url = this.location.path();
		this.accountDataService
			.confirmSignUp(url)
			.subscribe(
				result => signUpSuccessCallback(result),
				result => signUpErrorCallback(result)
			);
	}

	companyRegister(registrationData, successCallback, errorCallback) {
		this.accountDataService
			.signUp(registrationData)
			.subscribe(
				result => successCallback(result),
				result => errorCallback(result)
			);
	}

	private checkIfTermsOfUseAccepted(userData: User): void {
		const termsUpdatedModal = this.modalCtrl.create(TermsUpdatedModalComponent);

		termsUpdatedModal.onDidDismiss(data => {
			if (data === true) {
				this.accountDataService.acceptTermsOfUse().subscribe(
					() => {
						userData.termsOfUseAgree = true;
						this.getUserSuccessCallback(userData);
					},
					err => this.messageService.showDefaultErrorMessage(err)
				);
			} else {
				this.logoutMobile();
			}
		});

		termsUpdatedModal.present();
	}

	private errorAuthenticate(result): Observable<any> {
		const { error } = result;
		if (error.error === 'unauthorized' || error.error === 'invalid_grant') {
			this.messageService.showToaster('e_0', 'login');
		} else {
			this.messageService.showToaster('e_0', 'general');
		}
		return Observable.throw(error);
	}

	private getUserWebSuccessCallback(data: User): User {
		if (!data) {
			console.error('No user data');
			return;
		}

		const {
			USER_MENU_PATH,
			USER_HOME_PATH,
			SETTING_NAVBAR_PATH,
			ANONYMOUS_MENU_PATH,
			ANONYMOUS_HOME_PATH,
			ADMIN_MENU_PATH,
			ADMIN_HOME_PATH
		} = this.paths;

		this.user = data;
		const isAdmin = data.admin;
		if (data.notConfirmed) {
			this.setUserAuthenticated(false);
			this.setNavigationSettings(
				ANONYMOUS_MENU_PATH,
				ANONYMOUS_HOME_PATH,
				SETTING_NAVBAR_PATH
			);
		} else if (isAdmin) {
			this.setUserAuthenticated(true);
			this.settingsService.setDefaultTimeFormatSettings();
			this.setNavigationSettings(
				ADMIN_MENU_PATH,
				ADMIN_HOME_PATH,
				SETTING_NAVBAR_PATH
			);
		} else if (data.termsOfUseAgree !== true) {
			this.setUserAuthenticated(true);
			this.settingsService.getSettingsRequest().subscribe();
			this.getAndSetSubscriptionInfo(data);
			if (this.location.path() !== '/login') {
				this.logoutWeb();
			}
		} else if (data.expired) {
			this.location.go('/subscription');
			this.setUserAuthenticated(true);
			this.settingsService.getSettingsRequest().subscribe();
			this.getAndSetSubscriptionInfo(data);
			this.updateIntercomWhenExpaired(data);
		} else if (data.enabled) {
			this.setUserAuthenticated(true);
			this.settingsService.getSettingsRequest().subscribe();
			this.getAndSetSubscriptionInfo(data);
			this.setNavigationSettings(
				USER_MENU_PATH,
				USER_HOME_PATH,
				SETTING_NAVBAR_PATH
			);
			this.updateIntercom(data);
		} else {
			console.error('Anonymous User');
		}

		if (data.intercomCompany && data.intercomCompany.id) {
			this.companyId$.next(data.intercomCompany.id);
		}
		this.emitUser(this.user);

		return data;
	}

	get companyIdObservable() {
		return this.companyId$;
	}

	private getUserSuccessCallback(data: User): User {
		if (!data) {
			console.error('No user data');
			this.userData.error('No user data');
			return;
		}

		if (data === null) {
			return;
		}

		this.user = data;

		if (data.anonymous) {
			this.setUserAuthenticated(false);
			this.emitUser(this.user);
		} else {
			this.user = data;
			const isAdmin = data.admin;
			if (isAdmin) {
				this.setUserAuthenticated(true);
				this.emitUser(this.user);
			} else if (data.termsOfUseAgree !== true) {
				this.settingsService.getSettingsRequest().subscribe();
				this.checkIfTermsOfUseAccepted(data);
			} else if (data.expired && !data.anonymous) {
				this.setUserAuthenticated(true);
				this.getAndSetSubscriptionInfo(this.user);
				this.emitUser(this.user);
			} else if (data.enabled && !data.anonymous) {
				// this.removeBg = 'blah';
				this.settingsService.getSettingsRequest().subscribe();
				this.setUserAuthenticated(true);
				this.getAndSetSubscriptionInfo(this.user);
				this.emitUser(this.user);
			}
		}

		if (data.intercomCompany && data.intercomCompany.id) {
			this.companyId$.next(data.intercomCompany.id);
		}

		return data;
	}

	private getAndSetSubscriptionInfo(user: User): void {
		const {
			expired,
			inTrial,
			promotionActive,
			promocodeEnabled,
			subscriptionCancelled,
			subscriptionPeriodEnd,
			subscriptionPrice: {
				annualDiscountPrice,
				monthlyDiscountPrice,
				discount
			},
			subscription: { plan }
		} = user;

		const planInLowerCase = plan.toLowerCase();

		const isMonthlyPlan = [
			planInLowerCase === 'MONTHLY'.toLowerCase(),
			planInLowerCase === 'MONTHLY_DISCOUNT_25'.toLowerCase(),
			planInLowerCase === 'MONTHLY_DISCOUNT_50'.toLowerCase()
		].some(Boolean);

		const isAnnualPlan = [
			planInLowerCase === 'ANNUAL'.toLowerCase(),
			planInLowerCase === 'ANNUAL_DISCOUNT_25'.toLowerCase(),
			planInLowerCase === 'ANNUAL_DISCOUNT_50'.toLowerCase()
		].some(Boolean);

		this.isSubscribedAnnual = [subscriptionPeriodEnd, isAnnualPlan].every(
			Boolean
		);
		this.isSubscribedMonthly = [subscriptionPeriodEnd, isMonthlyPlan].every(
			Boolean
		);

		const isReferralDiscountHidden =
			discount && discount.causedByReferral && !promotionActive;

		const isSubscribedForMonthlyDiscount =
			(monthlyDiscountPrice || monthlyDiscountPrice === 0) &&
			this.isSubscribedMonthly;

		const isSubscribedForAnnualDiscount =
			(annualDiscountPrice || annualDiscountPrice === 0) &&
			this.isSubscribedAnnual;

		const isMonthlyPromotion =
			(monthlyDiscountPrice || monthlyDiscountPrice === 0) && inTrial;
		const isAnnualPromotion =
			(annualDiscountPrice || annualDiscountPrice === 0) && inTrial;

		this.isMonthlyPromoOrDiscountEnabled = [
			isMonthlyPromotion,
			subscriptionCancelled && !promocodeEnabled,
			expired && !isReferralDiscountHidden,
			inTrial && !isReferralDiscountHidden
		].some(Boolean);

		this.isAnnualPromoOrDiscountEnabled = [
			isAnnualPromotion,
			subscriptionCancelled && !promocodeEnabled,
			expired && !isReferralDiscountHidden,
			inTrial && !isReferralDiscountHidden
		].some(Boolean);

		// this.isMonthlyDiscount = [
		// 	isSubscribedForMonthlyDiscount,
		// 	isMonthlyPromotion
		// ].some(Boolean);

		// this.isAnnualDiscount = [
		// 	isSubscribedForAnnualDiscount,
		// 	isAnnualPromotion
		// ].some(Boolean);

		this.isMonthlyDiscount =
			monthlyDiscountPrice && monthlyDiscountPrice !== null;

		this.isAnnualDiscount = annualDiscountPrice && annualDiscountPrice !== null;

		this.setUserSubscription();
		this.emitSubscriptionInfo(this.subscription);
	}

	private setUserSubscription(): void {
		this.subscription = {
			monthlyDiscount: this.isMonthlyDiscount,
			annualDiscount: this.isAnnualDiscount,
			subscribedAnnual: this.isSubscribedAnnual,
			subscribedMonthly: this.isSubscribedMonthly,
			annualPromo: this.isAnnualPromoOrDiscountEnabled,
			monthlyPromo: this.isMonthlyPromoOrDiscountEnabled
		};
	}

	private updateIntercom(data) {
		const {
			id,
			firstName,
			email,
			registeredAt,
			subscription,
			intercomCompany
		} = data;

		Intercom('update', {
			user_id: id,
			name: firstName,
			email,
			created_at: registeredAt,
			plan: subscription.plan, // this must be customer attribute
			company: {
				id: intercomCompany.id,
				name: intercomCompany.name,
				email: intercomCompany.email,
				created_at: intercomCompany.createdAt,
				plan: intercomCompany.plan,
				jobs: intercomCompany.jobsCount,
				invoices: intercomCompany.invoiceCount,
				quotes: intercomCompany.quoteCount,
				contracts: intercomCompany.contractCount,
				questionnaires: intercomCompany.questionnaireCount,
				promo_code: intercomCompany.companyPromoCode
			}
		});
	}

	private updateIntercomWhenExpaired(data) {
		const { id, firstName, email, registeredAt } = data;

		Intercom('update', {
			user_id: id,
			name: firstName,
			email,
			created_at: registeredAt,
			plan: 'EXPIRED'
		});
	}

	setNavigationSettings = (
		menuPath: string,
		homePath?: string,
		navbarPath = 'authenticatedUser'
	) => {
		const path = this.location.path();
		if (path.includes('/subscription') || path.includes('/reactivate')) {
			menuPath = null;
			homePath = null;
		}

		this.navigationData.next({
			menu_path: menuPath,
			home_path: homePath || menuPath,
			settings_navbar_path: navbarPath
		});
	};

	getInvitationCompany() {
		const confirmPathPart = 'register/';
		const url = this.location.path();
		const index = url.indexOf(confirmPathPart);
		let token = null;

		if (index !== -1 && index + confirmPathPart.length < url.length) {
			token = url.substring(confirmPathPart.length + index, url.length);
		}

		if (token) {
			return this.accountDataService.getInvitationCompany(token);
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=nfw9yna8xro!./shared/services/user/account.service.ts